import type { TFunction } from 'i18next'

import { Badge, HStack, Text } from '@enechain/ecloud-designsystem'

import type { MasterDataTableColumn } from '~/components/common/MasterDataTable'
import type { CivilDate } from '~/models/civilDate'
import { formatCivilDateSlash } from '~/models/civilDate'
import { formatJstDateTimeHm } from '~/models/dateTime'
import { ContractStatusBadge } from '~/pages/CustomerInfo/components/ContractStatusBadge'
import { SupplyPointNumberCell } from '~/pages/CustomerInfo/components/SupplyPointNumberCell'
import { VoltageBadges } from '~/pages/CustomerInfo/components/VoltageBadges'
import {
  deriveContractStatus,
  formatKwDecimal,
} from '~/pages/CustomerInfo/models/display'
import { upcomingKwChanges } from '~/pages/CustomerInfo/models/kwPeriods'
import type { SupplyPoint } from '~/pages/CustomerInfo/models/types'

/** 契約一覧の列 (AC-CIN-027)。 操作列は表側 (`renderRowActions`) で足す。 */
export const buildContractColumns = (
  t: TFunction,
  today: CivilDate,
  resolveMemberName: (memberId: string | undefined) => string | undefined,
): Array<MasterDataTableColumn<SupplyPoint>> => {
  const none = t('page:customerInfo.common.none')
  return [
    {
      key: 'customerName',
      header: t('page:customerInfo.contract.column.customerName'),
      cell: (row) => (
        <HStack gap={1} whiteSpace="nowrap">
          <Text as="span">{row.customerName}</Text>
          {row.privateGeneration.has && (
            <Badge status="info">
              {t('page:customerInfo.contract.column.selfGenBadge')}
            </Badge>
          )}
        </HStack>
      ),
    },
    {
      key: 'number',
      header: t('page:customerInfo.contract.column.number'),
      nowrap: true,
      cell: (row) => <SupplyPointNumberCell point={row} />,
    },
    {
      key: 'voltage',
      header: t('page:customerInfo.contract.column.voltage'),
      cell: (row) => <VoltageBadges voltages={[row.voltageClass]} />,
    },
    {
      key: 'contractKw',
      header: t('page:customerInfo.contract.column.contractKw'),
      format: 'number',
      nowrap: true,
      cell: (row) => {
        const upcoming = upcomingKwChanges(row.kwPeriods.slice(1), today)
        return upcoming.length === 0 ? (
          formatKwDecimal(row.contractKw)
        ) : (
          // 数字だけを出す (桁を他の行と揃える)。 予定の変更は点線の下線 + hover (今日より後の予定を日付順に 1 行ずつ)
          <Text
            as="span"
            textDecorationColor="Text.midEmphasis"
            textDecorationLine="underline"
            textDecorationStyle="dotted"
            title={upcoming
              .map((k) =>
                t('page:customerInfo.kwPeriod.scheduled', {
                  date: formatCivilDateSlash(k.start),
                  kw: formatKwDecimal(k.kw),
                }),
              )
              .join('\n')}
          >
            {formatKwDecimal(row.contractKw)}
          </Text>
        )
      },
    },
    {
      key: 'retailer',
      header: t('page:customerInfo.contract.column.retailer'),
      cell: (row) => row.retailerName ?? none,
    },
    {
      key: 'start',
      header: t('page:customerInfo.contract.column.start'),
      nowrap: true,
      cell: (row) => formatCivilDateSlash(row.supplyStartDate),
    },
    {
      key: 'end',
      header: t('page:customerInfo.contract.column.end'),
      nowrap: true,
      cell: (row) =>
        row.supplyEndDate === undefined
          ? none
          : formatCivilDateSlash(row.supplyEndDate),
    },
    {
      key: 'status',
      header: t('page:customerInfo.contract.column.status'),
      nowrap: true,
      cell: (row) => (
        <ContractStatusBadge
          point={row}
          status={deriveContractStatus(row, today)}
        />
      ),
    },
    {
      key: 'updatedAt',
      header: t('page:customerInfo.contract.column.updatedAt'),
      nowrap: true,
      cell: (row) => formatJstDateTimeHm(row.updatedAt),
    },
    {
      key: 'updatedBy',
      header: t('page:customerInfo.contract.column.updatedBy'),
      nowrap: true,
      cell: (row) => resolveMemberName(row.updatedBy) ?? none,
    },
  ]
}
